<template>

	<label for="media" class="form-label">
		Upload Files
	</label>

	<div :class="'card' + (hasError('media') ? ' border-danger is-invalid' : '')">
		<div class="card-body">
			<uppy-drag-drop :uppy="uppy"/>

			<div v-if="filesUploading.length || filesUploaded.length" class="list-group mt-3">

				<div v-for="(file, index) in filesUploaded" :key="file.id" class="list-group-item">
					<div class="row align-items-center">
						<div class="col">
							<span class="fw-bold">{{ file.name }}</span>
							<span class="badge bg-success ms-2">Complete</span><br/>
							<small class="text-muted">{{ formatBytes(file.size) }}</small>
						</div>
						<div class="col-auto">
							<button class="btn btn-sm btn-rounded-circle btn-white" @click="deleteUpload(file.s3Multipart.key, file.s3Multipart.uploadId)">
								<span class="fe fe-x"></span>
							</button>
						</div>
					</div><!--/.row-->
				</div><!--/.list-group-item-->

				<div v-for="(file, index) in filesUploading" :key="file.id" class="list-group-item">
					<div class="row">
						<div class="col">
							<span class="fw-bold">{{ file.name }}</span>

							<span class="badge bg-light ms-2">
								{{ Math.round(file.progress.percentage) }}%
							</span>
							<br/>

							<small class="text-muted">{{ formatBytes(file.size) }}</small>
						</div>
					</div><!--/.row-->
					<div class="progress progress-sm mt-3">
						<div class="progress-bar" role="progressbar"
							:style="'width: ' + file.progress.percentage + '%'"
							:aria-valuenow="file.progress.percentage"
							aria-valuemin="0" aria-valuemax="100"></div>
					</div>
				</div><!--/.list-group-item-->

			</div><!--/.list-group-->

		</div>
	</div>
	<div v-if="hasError('media')" class="invalid-feedback">{{ errors.media[0] }}</div>

	<hr class="mt-4 mb-4">

	<div class="form-group">
		<label for="title" class="form-label">
			Title
			<small v-if="deliverable.title_is_required" class="text-muted">(required)</small>
			<small v-if="!deliverable.title_is_required" class="text-muted">(optional)</small>
		</label>
		<textarea v-model="title"
			rows="1" data-autosize
			:class="'form-control' + (hasError('title') ? ' is-invalid' : '')"
		></textarea>

		<div v-if="hasError('title')" class="invalid-feedback">
			{{ errors.title[0] }}
		</div>
	</div>

	<div class="form-group">
		<label for="caption" class="form-label">
			Caption
			<small v-if="deliverable.caption_is_required" class="text-muted">(required)</small>
			<small v-if="!deliverable.caption_is_required" class="text-muted">(optional)</small>
		</label>
		<textarea v-model="caption"
			rows="1" data-autosize
			:class="'form-control' + (hasError('caption') ? ' is-invalid' : '')"
		></textarea>

		<div v-if="hasError('caption')" class="invalid-feedback">
			{{ errors.caption[0] }}
		</div>
	</div>

	<hr class="mt-4 mb-5">

	<button :class="buttonClasses" @click.prevent="submit">
		<div v-if="this.isSubmitting"
			class="spinner-border text-white"
			role="status">
			<span class="visually-hidden">Loading...</span>
		</div>
		<div v-else>Submit For Review</div>
	</button>
	<a class="btn btn-lg w-100 btn-link text-muted" :href="redirect">Cancel</a>

</template>


<script>
	import { DragDrop } from '@uppy/vue';

	import '@uppy/core/dist/style.css'
	import '@uppy/dashboard/dist/style.css'

	import autosize from 'autosize';
	import Uppy from '@uppy/core'
	import AwsS3Multipart from '@uppy/aws-s3-multipart'

	export default {

		props: ['deliverableData', 'creativeAssetsEndpoint', 'redirect'],

		components: {
			'uppy-drag-drop': DragDrop,
		},

		data() {
			return {
				deliverable: this.deliverableData,

				filesUploading: [],
				filesUploaded: [],

				title: null,
				caption: null,
				hashtags: null,

				errors: null,
				isSubmitting: false,
			}
		},

		mounted() {

			/*
			Initialize autosize textareas
			=================================*/
			const autosizeTextareas = document.querySelectorAll('[data-autosize]');

			autosizeTextareas.forEach((autosizeTextarea) => {
				autosize(autosizeTextarea);
			});

			/*
			Register Uppy event listeners
			=================================*/
			this.uppy.on('file-added', (file) => {
				// add file to filesUploading array
				this.filesUploading.push(file);
			});

			this.uppy.on('upload', (data) => {
				// console.log('upload', data);
			});

			this.uppy.on('upload-progress', (file, progress) => {
				for (var i = 0; i < this.filesUploading.length; i++) {
					if(this.filesUploading[i].id == file.id) {
						this.filesUploading[i].progress.percentage = progress.bytesUploaded / progress.bytesTotal * 100;
					}
				}
			});

			this.uppy.on('upload-success', (file, response) => {
				this.handleUploadSuccess(file, response);
			});

			this.uppy.on('complete', (result) => {
				// console.log('complete');
				// console.log('successful files:', result.successful);
				// console.log('failed files:', result.failed);
			});

			this.uppy.on('error', (error) => {
				console.error(error);
			});

			this.uppy.on('upload-error', (file, error, response) => {
				console.log('upload-error');
				console.log('error with file:', file.id);
				console.log('error message:', error);
			});

		},

		computed: {

			uppy() {
				// initialize Uppy
				const uppy = new Uppy({
					// https://uppy.io/docs/uppy/
					id: 'uppyDeliverable' + this.deliverable.id,
					autoProceed: true,
					// allowMultipleUploadBatches: true,
					debug: true,
					inline: true,
					meta: {
						deliverableId: this.deliverable.id
					},
					restrictions: {
						allowedFileTypes: this.allowedFileTypes,
						maxFileSize: 1000 * 1000 * 1000 * 1000 * 2, /* 2 GB */
						maxNumberOfFiles: 20,
						minNumberOfFiles: 1,
					}
				}).use(AwsS3Multipart, {
					limit: 5,
					companionUrl: '/',
					companionHeaders:
					{
						'X-CSRF-TOKEN': window.csrfToken,
					},
				});

				return uppy;
			},

			icon() {
				if(this.deliverable.type == 'video') {
					return '<span class="fe fe-film"></span>';
				}
				else if(this.deliverable.type == 'image') {
					return '<span class="fe fe-image"></span>';
				}
				else {
					return '<span class="fe fe-help-circle"></span>';
				}
			},

			allowedFileTypes() {
				if(this.deliverable.type == 'video') {
					return ['video/*', 'audio/*'];
				}
				else if(this.deliverable.type == 'image') {
					return ['image/*'];
				}
				else {
					return ['image/*', 'video/*', 'audio/*'];
				}
			},

			buttonClasses() {
				var classes = 'btn btn-lg w-100 mb-3 ';
				if(this.isSubmitting) {
					return classes + 'btn-primary disabled';
				}
				else if(this.filesUploaded.length > 0) {
					return classes + 'btn-primary';
				}
				else {
					return classes + 'btn-secondary disabled';
				}
			},

		},

		methods: {

			submit() {
				this.isSubmitting = true;

				axios.post(this.creativeAssetsEndpoint, {
					media: this.filesUploaded,
					title: this.title,
					caption: this.caption,
					hashtags: this.hashtags,
				}).then((response) => {
					location.href = this.redirect;
				})
				.catch((e) => {
					this.isSubmitting = false;
					console.log(e.response.data.errors);
					this.errors = e.response.data.errors;
				});
			},

			handleUploadSuccess(file, response) {

				// remove from filesUploading array
				for (var i = 0; i < this.filesUploading.length; i++)
				{
					if(this.filesUploading[i].id == file.id) {
						this.filesUploading.splice(i, 1);
					}
				}

				// add to filesUploaded array
				this.filesUploaded.push(file);
			},

			hasError(field) {
				if(this.errors && typeof this.errors[field] !== 'undefined') {
					return true;
				}
				return false;
			},

			deleteUpload(key, uploadId) {
				axios.delete('/s3/multipart/' + uploadId + '?key=' + key)
					.then((response) => {
						// console.log(response);

						// remove from filesUploaded array
						for (var i = 0; i < this.filesUploaded.length; i++)
						{
							if(this.filesUploaded[i].s3Multipart.key == key) {
								this.filesUploaded.splice(i, 1);
							}
						}
					})
					.catch((error) => {
						console.error(error);
					});
			},

			isLowRes(width, height) {
				if(width > 0 && height > 0 && (width * height < 1920 * 1080)) {
					return true;
				}
				else {
					return false;
				}
			},

			formatBytes(bytes, decimals = 0) {
				if (bytes === 0) return '0 bytes';
				const k = 1024;
				const dm = decimals < 0 ? 0 : decimals;
				const sizes = ['bytes', 'kb', 'mb', 'gb', 'tb', 'pb', 'eb', 'zb', 'yb'];
				const i = Math.floor(Math.log(bytes) / Math.log(k));
				return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
			},

			nl2br(str, is_xhtml) {
				var breakTag = (is_xhtml || typeof is_xhtml === 'undefined') ? '<br />'   : '<br>';
				return (str + '').replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '$1' + breakTag   + '$2');
			}
		},

		beforeDestroy () {
			uppy.close()
		}
	}

</script>
